import { z } from 'zod'

/**
 * Apple team access: discovering the Apple teams our shared Apple ID can reach, and
 * minting an App Store Connect API key on one of them.
 *
 * A school invites `apps@schooljourney.com` to its Apple organization. From then on
 * two Apple reads describe the team without any key:
 *
 * - the Developer portal's team list (`listTeams.action`): team id, name, type,
 *   status, membership expiry and the Account Holder (`teamAgent`);
 * - App Store Connect's `olympus/v1/actors?include=provider`: per provider, its
 *   `developerTeamId`, our App Store Connect roles, and the provider's UUID, which
 *   is the API key Issuer ID.
 *
 * The two can disagree about our role: Hockaday's portal lists us as TEAM_ADMIN
 * while App Store Connect has us as APP_MANAGER (App Manager with certificate
 * access). Only the App Store Connect role decides whether we can create an API
 * key, so that is the one `canCreateApiKey` reads.
 *
 * Both jobs run on the one-shot browser task over the stored `apple-spaceship`
 * session. The task reports its result as the execution row's `result`, which is
 * what the status routes read; nothing here has a table of its own.
 */

export const APPLE_TEAM_DISCOVERY_JOB_TYPE = 'APPLE_TEAM_DISCOVERY' as const
export const ASC_API_KEY_CREATE_JOB_TYPE = 'ASC_API_KEY_CREATE' as const

/** App Store Connect roles that may create a team API key. */
export const ASC_KEY_CREATOR_ROLES = ['ADMIN', 'ACCOUNT_HOLDER'] as const

/** The nickname every key we mint carries, so a later run recognizes its own key. */
export const ASC_API_KEY_NICKNAME = 'School Journey'

export const AppleTeamAccountHolderSchema = z.object({
  name: z.string().nullable(),
  email: z.string().nullable()
})
export type AppleTeamAccountHolder = z.infer<typeof AppleTeamAccountHolderSchema>

/** One team, as the runner read it. */
export const AppleDiscoveredTeamSchema = z.object({
  /** Developer team id, e.g. `5XF5WKU5JF`. Same value as `PublishingAccount.accountId`. */
  teamId: z.string().min(1),
  name: z.string(),
  /** From the portal `type` (`Company/Organization` | `Individual`); null when unrecognized. */
  kind: z.enum(['organization', 'personal']).nullable(),
  /** Portal team status, e.g. `active`. */
  portalStatus: z.string().nullable(),
  /** Apple Developer Program expiry as `YYYY-MM-DD`; null when absent or unparseable. */
  membershipExpiresOn: z.string().nullable(),
  accountHolder: AppleTeamAccountHolderSchema.nullable(),
  /** Our roles in the Developer portal, e.g. `TEAM_ADMIN`. */
  portalRoles: z.array(z.string()),
  /** Our roles in App Store Connect, e.g. `ADMIN`, `APP_MANAGER`. Empty when no provider matched the team. */
  ascRoles: z.array(z.string()),
  /** App Store Connect provider UUID = API key Issuer ID. Null when no provider matched the team. */
  issuerId: z.string().nullable(),
  /** App Store Connect numeric provider id (`internalId`). */
  ascProviderId: z.string().nullable()
})
export type AppleDiscoveredTeam = z.infer<typeof AppleDiscoveredTeamSchema>

/** What an `APPLE_TEAM_DISCOVERY` job writes to its execution row's `result`. */
export const AppleTeamDiscoveryResultSchema = z.object({
  kind: z.literal('apple_team_discovery'),
  observedAt: z.string(),
  teams: z.array(AppleDiscoveredTeamSchema)
})
export type AppleTeamDiscoveryResult = z.infer<typeof AppleTeamDiscoveryResultSchema>

/**
 * Why a team cannot get an API key from us yet. Each code maps to one thing the
 * school (or we) must do; `message` is that instruction in plain words.
 */
export const AppleTeamBlockerCodeSchema = z.enum([
  /** Our App Store Connect role is not Admin. The school changes `apps@schooljourney.com` to Admin in Users and Access. */
  'asc_role_not_admin',
  /** No App Store Connect provider in our session matches this team id. */
  'no_asc_access',
  /** The portal reports the team or its membership as not active. */
  'membership_inactive',
  /** API access was never requested. Only the Account Holder can click Request Access in Users and Access → Integrations. */
  'api_access_not_requested',
  /** API access was requested and Apple has not approved it yet. */
  'api_access_pending',
  /** The stored Apple session could not be opened. */
  'session_unavailable'
])
export type AppleTeamBlockerCode = z.infer<typeof AppleTeamBlockerCodeSchema>

export const AppleTeamBlockerSchema = z.object({
  code: AppleTeamBlockerCodeSchema,
  message: z.string()
})
export type AppleTeamBlocker = z.infer<typeof AppleTeamBlockerSchema>

/** What an `ASC_API_KEY_CREATE` job writes to its execution row's `result`. Never key material. */
export const AscApiKeyCreateResultSchema = z.object({
  kind: z.literal('asc_api_key_create'),
  accountId: z.string(),
  /**
   * `created`: a new key was minted and vaulted.
   * `already_vaulted`: a key in our vault is still active on Apple; nothing was created.
   * `blocked`: a precondition failed; see `blocker`. Nothing was created.
   */
  outcome: z.enum(['created', 'already_vaulted', 'blocked']),
  keyId: z.string().nullable(),
  issuerId: z.string().nullable(),
  blocker: AppleTeamBlockerSchema.nullable()
})
export type AscApiKeyCreateResult = z.infer<typeof AscApiKeyCreateResultSchema>

/** Execution status as the web sees it. `processing` maps to `running`; `skipped` maps to `failed`. */
export const AppleJobStatusSchema = z.enum(['queued', 'running', 'completed', 'failed'])
export type AppleJobStatus = z.infer<typeof AppleJobStatusSchema>

// ---------------------------------------------------------------------------
// HTTP: POST /v1/mobile-apps/publishing-accounts/apple-team-discovery
//       GET  /v1/mobile-apps/publishing-accounts/apple-team-discovery/:executionId
// ---------------------------------------------------------------------------

export const StartAppleJobResponseSchema = z.object({
  data: z.object({ executionId: z.string() }),
  message: z.string()
})
export type StartAppleJobResponse = z.infer<typeof StartAppleJobResponseSchema>

/** A discovered team joined with the registry. */
export const AppleTeamDiscoveryTeamViewSchema = AppleDiscoveredTeamSchema.extend({
  /** `PublishingAccount.publicId` when this team is already registered; null when it is new. */
  publishingAccountId: z.string().nullable(),
  /** True when `ascRoles` holds a role in `ASC_KEY_CREATOR_ROLES` and nothing in `blockers` stops it. */
  canCreateApiKey: z.boolean(),
  blockers: z.array(AppleTeamBlockerSchema)
})
export type AppleTeamDiscoveryTeamView = z.infer<typeof AppleTeamDiscoveryTeamViewSchema>

export const AppleTeamDiscoveryStatusSchema = z.object({
  executionId: z.string(),
  status: AppleJobStatusSchema,
  /** The execution's error message or summary when `failed`. */
  error: z.string().nullable(),
  observedAt: z.string().nullable(),
  /** Unregistered teams first, then by name. Empty until `completed`. */
  teams: z.array(AppleTeamDiscoveryTeamViewSchema)
})
export type AppleTeamDiscoveryStatus = z.infer<typeof AppleTeamDiscoveryStatusSchema>

export const GetAppleTeamDiscoveryResponseSchema = z.object({
  data: AppleTeamDiscoveryStatusSchema,
  message: z.string()
})
export type GetAppleTeamDiscoveryResponse = z.infer<typeof GetAppleTeamDiscoveryResponseSchema>

// ---------------------------------------------------------------------------
// HTTP: POST /v1/mobile-apps/publishing-accounts/:publicId/asc-api-key
//       GET  /v1/mobile-apps/publishing-accounts/:publicId/asc-api-key/:executionId
// ---------------------------------------------------------------------------

export const AscApiKeyCreateStatusSchema = z.object({
  executionId: z.string(),
  status: AppleJobStatusSchema,
  error: z.string().nullable(),
  /** Null until `completed`. */
  result: AscApiKeyCreateResultSchema.nullable()
})
export type AscApiKeyCreateStatus = z.infer<typeof AscApiKeyCreateStatusSchema>

export const GetAscApiKeyCreateResponseSchema = z.object({
  data: AscApiKeyCreateStatusSchema,
  message: z.string()
})
export type GetAscApiKeyCreateResponse = z.infer<typeof GetAscApiKeyCreateResponseSchema>

/** The shared Apple ID schools invite. */
export const SCHOOL_JOURNEY_APPLE_ID = 'apps@schooljourney.com'

export interface AppleTeamBlockerContext {
  teamName?: string | null
  accountHolderName?: string | null
  ascRoles?: readonly string[]
}

/** One blocker with the instruction an operator can forward to the school as written. */
export function appleTeamBlocker(code: AppleTeamBlockerCode, context: AppleTeamBlockerContext = {}): AppleTeamBlocker {
  const team = context.teamName?.trim() || 'this team'
  const holder = context.accountHolderName?.trim()
    ? `the Account Holder (${context.accountHolderName.trim()})`
    : 'the Account Holder'
  const roles = context.ascRoles?.length ? context.ascRoles.join(', ').replaceAll('_', ' ').toLowerCase() : null
  switch (code) {
    case 'asc_role_not_admin':
      return {
        code,
        message: `In App Store Connect, ${SCHOOL_JOURNEY_APPLE_ID} ${roles ? `has the role "${roles}"` : 'is not an Admin'} on ${team}. Ask ${holder} or an Admin to change it to Admin in Users and Access → People.`
      }
    case 'no_asc_access':
      return {
        code,
        message: `${SCHOOL_JOURNEY_APPLE_ID} has no App Store Connect access to ${team}. Ask the school to invite it with the Admin role in Users and Access → People.`
      }
    case 'membership_inactive':
      return {
        code,
        message: `${team}'s Apple Developer membership is not active. ${holder} must renew it at developer.apple.com.`
      }
    case 'api_access_not_requested':
      return {
        code,
        message: `${team} has never turned on App Store Connect API access. Only ${holder} can do it: Users and Access → Integrations → App Store Connect API → Request Access.`
      }
    case 'api_access_pending':
      return {
        code,
        message: `${team} has requested App Store Connect API access and Apple has not approved it yet. Try again once Apple approves it.`
      }
    case 'session_unavailable':
      return {
        code,
        message: 'Our stored Apple session could not be opened. Renew the apple-spaceship session, then try again.'
      }
  }
}
